import { useMemo } from 'react';

import { useTriggerRepaint, yamlToJson } from '@cast/utils';

import { YamlLine } from '../YamlLine';
import { StyledScroller, SyntaxHighlight } from './YamlStyledComponents';
import { PreviewStateProvider } from '../../../_providers/PreviewStateProvider';
import { PreviewYamlProps } from '../../types';
import { useYaml } from '../../useYaml';

export const YamlObjectContainer = (props: PreviewYamlProps) => {
  const { containerSx, renderFallback, children, prefix } = props;
  let { data } = props;

  if (typeof data === 'string') {
    try {
      if (data === 'null' && renderFallback) {
        return <>{renderFallback(data)}</>;
      }
      data = yamlToJson(data);
    } catch (e) {
      if (!renderFallback) {
        throw e;
      }
      return <>{renderFallback(data)}</>;
    }
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const yaml = useYaml(data as object);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const yamlText = useMemo(() => yaml?.yamlAsString(), [yaml]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const repaint = useTriggerRepaint();

  const linesCount = yaml?.allNodes.length ?? 0;

  return (
    <PreviewStateProvider formattedCode={yamlText}>
      {children ? (
        typeof children === 'function' ? (
          <>{children({ formattedCode: yamlText })}</>
        ) : (
          <>{children}</>
        )
      ) : null}

      <StyledScroller ownerState={props}>
        <SyntaxHighlight ownerState={props} sx={containerSx}>
          {prefix}
          {yaml?.allNodes.map((line, index) => (
            <YamlLine
              key={index}
              line={line}
              onToggle={repaint}
              linesCount={linesCount}
            />
          ))}
        </SyntaxHighlight>
      </StyledScroller>
    </PreviewStateProvider>
  );
};
